import React, { useEffect, useState } from 'react';
import {
  Option, useApiClient, useFetch,
} from '@marageti/z4-lib';
import { PartnerSalesRecordingTotals } from '@marageti/z4-sdk/lib/travel/SalesRecording';
import { useLocation, useNavigate } from 'react-router-dom';
import InvoiceView from './invoice-view';
import { formatDateToYYYYMM, parseDateFromYYYYMM } from '../utils';

// Define today's date for default date selection and a default status option.
const today = new Date();
const defaultStatusOption = { title: 'All', value: 'ALL' };

// Custom hook to parse URL query parameters.
const useQuery = () => new URLSearchParams(useLocation().search);

const InvoiceContainer = () => {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const query = useQuery();
  const location = useLocation();

  // Define options for the status select input.
  const statusOptions = [
    { title: 'All', value: 'ALL' },
    { title: 'Unapproved', value: 'unapproved' },
    { title: 'Approved', value: 'approved' },
    { title: 'Invoiced', value: 'invoiced' },
  ];

  // Initial state setup based on URL query parameters.
  const initialMonth = query.get('month');
  const initialStatus = query.get('status') || defaultStatusOption.value;

  // State for selected month and status, initialized from URL parameters or defaults.
  const [selectedMonth, setSelectedMonth] = useState<Date | null>(initialMonth ? new Date(initialMonth) : today);
  const [selectedStatus, setSelectedStatus] = useState<Option>(statusOptions.find((option) => option.value === initialStatus) || defaultStatusOption);

  // Constructs the request parameters for the API call based on the selected month and status.
  const searchRequest = {
    ...selectedMonth && { month: formatDateToYYYYMM(selectedMonth) },
    ...selectedStatus.value !== 'ALL' && { status: selectedStatus.value },
  };

  const {
    data: salesRecordingsData,
    isLoading: salesLoading,
  } = useFetch<PartnerSalesRecordingTotals>(apiClient.partnersClient.getSalesRecordingTotalsForMonth, [{ params: searchRequest }], true);

  // Updates the URL and navigates to reflect changes in month or status.
  const navigateWithParams = (month: Date | null, status: Option) => {
    const formattedMonth = month ? formatDateToYYYYMM(month) : '';
    navigate(`/sales/invoices?month=${formattedMonth}&status=${status.value}`, { replace: true });
  };

  // Handlers for when month or status selection changes.
  const onChangeStatus = (value: Option) => {
    setSelectedStatus(value);
    navigateWithParams(selectedMonth, value);
  };

  const onChangeMonth = (value: Date | null) => {
    setSelectedMonth(value || today);
    navigateWithParams(value, selectedStatus);
  };

  // Effect hook to update component state based on URL changes.
  useEffect(() => {
    const monthFromUrl = query.get('month');
    const statusFromUrl = query.get('status') || defaultStatusOption.value;
    if (monthFromUrl) {
      setSelectedMonth(parseDateFromYYYYMM(monthFromUrl));
    } else {
      setSelectedMonth(today);
    }
    setSelectedStatus(statusOptions.find((option) => option.value === statusFromUrl) || defaultStatusOption);
  }, [location.search]);

  return (
    <InvoiceView
      loading={salesLoading}
      onChangeMonth={onChangeMonth}
      onChangeStatus={onChangeStatus}
      sales={salesRecordingsData?.partners || []}
      selectedMonth={selectedMonth}
      selectedStatus={selectedStatus}
      statusOptions={statusOptions}
    />
  );
};

export default InvoiceContainer;
